const DAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
const WEEKS = ["first", "second", "third", "fourth", "last"];
const EMPTY_METADATA = { selection_criteria: "", asset_type: "", asset_ids: [] };

const getFileFormats = (config) => {
  const formats = config?.file_format ?? config?.format ?? "csv";
  return (Array.isArray(formats) ? formats : formats.split(","))
    .map((format) => format.trim().replace("csv zip", "csv_zip"))
    .filter(Boolean);
};

const toDisplayTime = (time) => {
  if (!time) return "12:00 AM";
  const [hours, minutes] = time.split(":").map(Number);
  return `${String(hours % 12 || 12).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${hours < 12 ? "AM" : "PM"}`;
};

const toApiTime = (time) => {
  const match = /^(0?[1-9]|1[0-2]):([0-5]\d)\s*(AM|PM)$/i.exec(time?.trim() || "");
  if (!match) return null;
  const hours = Number(match[1]) % 12 + (match[3].toUpperCase() === "PM" ? 12 : 0);
  return `${String(hours).padStart(2, "0")}:${match[2]}`;
};

export const getScheduledReportValues = (config) => ({
  frequency: config?.frequency || "never",
  selected_days: (config?.days_of_week || []).map((day) => DAYS[day]).filter(Boolean),
  send_every_weeks: (Array.isArray(config?.week_of_month) ? config.week_of_month : [config?.week_of_month])
    .map((week) => WEEKS[week - 1]).filter(Boolean),
  send_time: toDisplayTime(config?.delivery_time),
  date_range: config?.date_range?.toLowerCase() || "weekly",
  start_time: config?.start_time ?? null,
  end_time: config?.end_time ?? null,
  delivery_method: "email",
  user_group_ids: config?.user_group_id ? [config.user_group_id] : [],
  report_type: config ? config.report_type || "" : "trackpoint",
  report_sub_type: config?.report_sub_type || "",
  event_type_id: config?.event_type_id || "",
  event_type: config?.event_type || "",
  report_configuration_id: config?.report_configuration_id || "",
  summary_interval: config?.summary_interval || "",
  group_by_asset: config?.group_by_asset ?? false,
  metadata: {
    ...EMPTY_METADATA,
    selection_criteria: config?.selection_criteria || "",
    asset_type: config?.asset_type || "",
    asset_ids: config?.asset_ids || [],
  },
  file_format: getFileFormats(config),
  legacy_format: config?.format || null,
  send_if_empty: config?.send_if_empty ?? false,
});

export const validateScheduledReportValues = (values) => {
  if (values.frequency === "never") return {};
  const errors = {};
  if (!["daily", "weekly", "monthly"].includes(values.frequency)) {
    errors.frequency = "Choose a valid frequency.";
  }
  if (values.frequency !== "daily" &&
      (!values.selected_days?.length || values.selected_days.some((day) => !DAYS.includes(day)))) {
    errors.selected_days = "Choose at least one day.";
  }
  if (values.frequency === "monthly" &&
      (!values.send_every_weeks?.length || values.send_every_weeks.some((week) => !WEEKS.includes(week)))) {
    errors.send_every_weeks = "Choose at least one week of the month.";
  }
  if (!toApiTime(values.send_time)) errors.send_time = "Use a valid time such as 09:30 AM.";
  if (!["daily", "weekly", "monthly", "custom"].includes(values.date_range)) errors.date_range = "Choose a valid date range.";
  if (values.date_range === "custom" &&
      (!Number.isInteger(values.start_time) || !Number.isInteger(values.end_time) ||
       values.start_time <= 0 || values.end_time <= values.start_time)) {
    errors.date_range = "Choose a valid custom date range.";
  }
  if (values.user_group_ids?.length !== 1 || !Number.isInteger(Number(values.user_group_ids[0])) || Number(values.user_group_ids[0]) <= 0) {
    errors.user_group_ids = "Choose one user group.";
  }
  if (values.report_type || values.report_sub_type || values.metadata?.selection_criteria ||
      values.metadata?.asset_type || values.metadata?.asset_ids?.length) {
    if (!values.report_type) errors.report_type = "Choose a report type.";
    if (values.report_type !== "vehicle_performance" && !values.report_sub_type) errors.report_sub_type = "Choose a report sub-type.";
    if (values.report_type === "event" && (!Number.isInteger(Number(values.event_type_id)) || Number(values.event_type_id) <= 0)) {
      errors.event_type_id = "Choose an event type.";
    }
    if (values.report_type === "trackpoint" && values.report_sub_type === "detailed" &&
        (!Number.isInteger(Number(values.report_configuration_id)) || Number(values.report_configuration_id) <= 0)) {
      errors.report_configuration_id = "Report Configuration is required.";
    }
    if (values.report_sub_type === "summary" && !["na", "daily", "weekly", "monthly"].includes(values.summary_interval)) {
      errors.summary_interval = "Choose a summary interval.";
    }
    if (!values.metadata?.selection_criteria) errors.selection_criteria = "Choose a selection criteria.";
    if (!values.metadata?.asset_type) errors.asset_type = "Choose an asset type.";
    if (!values.metadata?.asset_ids?.length) errors.asset_ids = "Choose at least one asset or group.";
    else if (values.metadata.asset_ids.some((id) => !Number.isInteger(Number(id)) || Number(id) <= 0)) {
      errors.asset_ids = "Choose valid assets or groups.";
    }
    if (!values.file_format?.length) errors.file_format = "Choose at least one file format.";
    else if (values.file_format.some((format) => !["csv", "pdf", "excel"].includes(format))) {
      errors.file_format = "Choose CSV, PDF, or XLSX.";
    }
  }
  return errors;
};

export const buildScheduledEmailPayload = (values, reportConfigurationId) => {
  const schedule = {
    frequency: values.frequency,
    days_of_week: values.frequency === "daily" ? [] : values.selected_days.map((day) => DAYS.indexOf(day)),
    week_of_month: ["monthly", "never"].includes(values.frequency) && values.send_every_weeks.length
      ? values.send_every_weeks.map((week) => WEEKS.indexOf(week) + 1) : null,
    delivery_time: toApiTime(values.send_time),
    date_range: values.date_range,
    ...(values.date_range === "custom" ? { start_time: values.start_time, end_time: values.end_time } : {}),
    user_group_id: Number(values.user_group_ids[0]),
    send_if_empty: values.send_if_empty,
  };
  if (!values.report_type) {
    return {
      ...schedule,
      ...(values.legacy_format ? { format: values.legacy_format } : {}),
      ...(reportConfigurationId ? { report_configuration_id: reportConfigurationId } : {}),
    };
  }
  return {
    ...schedule,
    report_type: values.report_type,
    report_sub_type: values.report_sub_type,
    ...(values.report_type === "event" ? {
      event_type_id: Number(values.event_type_id),
      ...(values.event_type ? { event_type: values.event_type } : {}),
    } : {}),
    ...(values.report_sub_type === "summary"
      ? { summary_interval: values.summary_interval, group_by_asset: values.group_by_asset } : {}),
    selection_criteria: values.metadata.selection_criteria,
    asset_type: values.metadata.asset_type,
    asset_ids: values.metadata.asset_ids.map(Number),
    file_format: values.file_format,
    ...(values.report_configuration_id || reportConfigurationId
      ? { report_configuration_id: values.report_type === "trackpoint" && values.report_sub_type === "detailed"
        ? Number(values.report_configuration_id) || null : null } : {}),
  };
};
